import { Component, Input, OnInit } from '@angular/core';
import { MediaLibraryFileModel } from '../../../../models/sales-funnel';

// Interface for the display configuration
interface ResourceDisplay {
  iconName: string; // The lucide icon name (e.g., 'image', 'file-text')
  iconClass: string; // The text color classes
  bgClass: string;   // The background color classes
  label: string;
}

@Component({
  selector: 'app-file-type-icon',
  templateUrl: './file-type-icon.component.html',
  styleUrls: ['./file-type-icon.component.css'],
})
export class FileTypeIconComponent implements OnInit {
  // Default State
  display: ResourceDisplay = {
    iconName: 'file-question',
    iconClass: 'text-gray-700 dark:text-gray-300',
    bgClass: 'bg-gray-200 dark:bg-gray-900',
    label: 'Unknown',
  };

  @Input('file') set setType(value: MediaLibraryFileModel | string) {
    let mime = '';
    let ext = '';

    // 1. Extract MIME and Extension safely
    if (typeof value !== 'string' && value?.fileInfo?.ext) {
      mime = value.type?.name || '';
      ext = value.fileInfo.ext.replace('.', '');
    } else if (typeof value === 'string') {
      if (value.includes('/')) {
        mime = value;
      } else {
        ext = value.replace('.', '');
      }
    }

    // 2. Normalize
    mime = mime.toLowerCase();
    ext = ext.toLowerCase();

    // 3. Resolve
    this.display = this.resolveDisplay(mime, ext);
  }

  // Optional: If you want to force specific sizes via input
  @Input() size: { width?: string; height?: string } = { width: 'h-5', height: 'w-5' };

  constructor() {}

  ngOnInit() {}

  private resolveDisplay(mime: string, ext: string): ResourceDisplay {
    // Helper to check against mime or fallback extension
    const matches = (mChecks: string[], eChecks: string[]) => {
      return mChecks.some(m => mime.includes(m)) || eChecks.includes(ext);
    };

    // --- MAPPING LOGIC START ---
    
    // 1. Image
    if (mime.startsWith('image/') || matches([], ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'])) {
      return {
        iconName: 'image',
        iconClass: 'text-pink-700 dark:text-pink-300',
        bgClass: 'bg-pink-100 dark:bg-pink-900',
        label: 'Image'
      };
    }

    // 2. Video
    if (mime.startsWith('video/') || matches([], ['mp4', 'mov', 'avi', 'mkv'])) {
      return {
        iconName: 'video',
        iconClass: 'text-purple-700 dark:text-purple-300',
        bgClass: 'bg-purple-100 dark:bg-purple-900',
        label: 'Video'
      };
    }

    // 3. Audio
    if (mime.startsWith('audio/') || matches([], ['mp3', 'wav', 'ogg'])) {
      return {
        iconName: 'music',
        iconClass: 'text-yellow-700 dark:text-yellow-300',
        bgClass: 'bg-yellow-100 dark:bg-yellow-900',
        label: 'Audio'
      };
    }

    // 4. Archive (Zip/Rar)
    if (matches(['zip', 'compressed', 'tar', 'gzip'], ['zip', 'rar', '7z', 'tar', 'gz'])) {
      return {
        iconName: 'file-archive',
        iconClass: 'text-orange-700 dark:text-orange-300',
        bgClass: 'bg-orange-100 dark:bg-orange-900',
        label: 'Archive'
      };
    }

    // 5. PDF
    if (mime === 'application/pdf' || mime.includes('pdf') || ext === 'pdf') {
      return {
        iconName: 'book-open', // React used BookOpen for PDF
        iconClass: 'text-red-700 dark:text-red-300',
        bgClass: 'bg-red-100 dark:bg-red-900',
        label: 'PDF'
      };
    }

    // 6. Word
    if (matches(['msword', 'wordprocessingml'], ['doc', 'docx'])) {
      return {
        iconName: 'book-open', // React used BookOpen for Word too
        iconClass: 'text-blue-700 dark:text-blue-300',
        bgClass: 'bg-blue-100 dark:bg-blue-900',
        label: 'Word Document'
      };
    }

    // 7. Spreadsheet
    if (matches(['spreadsheetml', 'excel', 'csv'], ['xls', 'xlsx', 'csv'])) {
      return {
        iconName: 'file-spreadsheet',
        iconClass: 'text-green-700 dark:text-green-300',
        bgClass: 'bg-green-100 dark:bg-green-900',
        label: 'Spreadsheet'
      };
    }

    // 8. Presentation
    if (matches(['presentationml', 'powerpoint'], ['ppt', 'pptx'])) {
      return {
        iconName: 'file-code', // React used FileCode (yellow) for PPT
        iconClass: 'text-yellow-700 dark:text-yellow-300',
        bgClass: 'bg-yellow-100 dark:bg-yellow-900',
        label: 'Presentation'
      };
    }

    // 9. Code
    if (matches(['json', 'xml', 'javascript', 'typescript', 'html', 'css'], ['js', 'ts', 'json', 'xml', 'html'])) {
      return {
        iconName: 'file-code',
        iconClass: 'text-fuchsia-700 dark:text-fuchsia-300',
        bgClass: 'bg-fuchsia-100 dark:bg-fuchsia-900',
        label: 'Code'
      };
    }

    // 10. Text (General)
    if (mime.startsWith('text/') || ext === 'txt') {
      return {
        iconName: 'file-text',
        iconClass: 'text-sky-700 dark:text-sky-300',
        bgClass: 'bg-sky-100 dark:bg-sky-900',
        label: 'Text Document'
      };
    }

    // Default Fallback
    return {
      iconName: 'file',
      iconClass: 'text-gray-700 dark:text-gray-300',
      bgClass: 'bg-gray-200 dark:bg-gray-900',
      label: 'File'
    };
  }
}